import { useActionQuery, useAction } from './hooks'
import type { ListSort } from '@/lib/sorting'

export const TASK_STATUSES = ['todo', 'in_progress', 'review', 'done', 'cancelled'] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]
export const TASK_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const
export type TaskPriority = (typeof TASK_PRIORITIES)[number]
export const SERIES_UNITS = ['minutes', 'hours', 'days', 'weeks', 'months', 'years'] as const
export type SeriesUnit = (typeof SERIES_UNITS)[number]
export const ENTITY_TYPES = ['client', 'pet', 'visit', 'journey', 'call', 'rating', 'insight'] as const

export interface Task {
  id: string
  title: string
  description: string
  status: TaskStatus
  priority: TaskPriority
  creator_id?: string
  creator_name: string
  assignee_id?: string
  assignee_name: string
  due_at?: string
  completed_at?: string
  series_id?: string
  occurrence_index?: number
  entity_type: string
  entity_id: string
  visibility: 'team' | 'private'
  source: string
  watchers: string[]
  comment_count: number
  overdue: boolean
  created_at: string
  updated_at: string
}

export interface TaskComment {
  id: string
  author_id?: string
  author_name: string
  body: string
  created_at: string
}

export interface TaskDetail {
  task: Task
  comments: TaskComment[]
  series_tasks: Task[]
}

export interface Series {
  id: string
  title: string
  description: string
  priority: TaskPriority
  assignee_id?: string
  unit: SeriesUnit
  interval: number
  starts_at: string
  ends_at?: string
  require_previous_complete: boolean
  active: boolean
  instance_count: number
}

/** TaskList column id → tasks.list sort key (the 500-task window is the top of the whole filtered set). */
export const TASK_SORT = { title: 'title', status: 'status', priority: 'priority', assignee: 'assignee', due: 'due', creator: 'creator', updated: 'updated' } as const

export interface TaskListFilter {
  query?: string
  mine?: boolean
  assignee_id?: string
  statuses?: TaskStatus[]
  entity_type?: string
  entity_id?: string
  due_from?: string
  due_to?: string
  overdue?: boolean
  limit?: number
  sort?: ListSort['sort']
  dir?: ListSort['dir']
}

export interface TaskCreateInput {
  title: string
  description?: string
  priority?: TaskPriority
  status?: TaskStatus
  assignee_id?: string
  due_at?: string
  entity_type?: string
  entity_id?: string
  visibility?: 'team' | 'private'
  watchers?: string[]
}

export interface TaskUpdateInput {
  task_id: string
  title?: string
  description?: string
  status?: TaskStatus
  priority?: TaskPriority
  assignee_id?: string
  due_at?: string
  clear_due?: boolean
  visibility?: 'team' | 'private'
}

export interface SeriesCreateInput {
  title: string
  description?: string
  priority?: TaskPriority
  assignee_id?: string
  unit: SeriesUnit
  interval?: number
  starts_at: string
  ends_at?: string
  require_previous_complete?: boolean
}

export const TasksA = {
  list: 'tasks.list',
  get: 'tasks.get',
  create: 'tasks.create',
  update: 'tasks.update',
  setStatus: 'tasks.set_status',
  delete: 'tasks.delete',
  comment: 'tasks.comment',
  watch: 'tasks.watch',
  seriesCreate: 'tasks.series_create',
  seriesList: 'tasks.series_list',
  seriesStop: 'tasks.series_stop',
} as const

export const useTasks = (f: TaskListFilter, enabled = true) => useActionQuery<Task[]>(TasksA.list, f, { enabled, placeholderData: (p) => p })
export const useTask = (id: string | undefined) => useActionQuery<TaskDetail>(TasksA.get, { task_id: id }, { enabled: !!id })
export const useSeriesList = () => useActionQuery<Series[]>(TasksA.seriesList, {})

export const useCreateTask = () => useAction<TaskCreateInput, Task>(TasksA.create, { formErrors: true })
export const useUpdateTask = () => useAction<TaskUpdateInput, Task>(TasksA.update, { formErrors: true })
export const useSetTaskStatus = () => useAction<{ task_id: string; status: TaskStatus }, Task>(TasksA.setStatus)
export const useDeleteTask = () => useAction<{ task_id: string }, { ok: boolean }>(TasksA.delete)
export const useCommentTask = () => useAction<{ task_id: string; body: string }, TaskComment>(TasksA.comment)
export const useWatchTask = () => useAction<{ task_id: string; watch: boolean }, Task>(TasksA.watch)
export const useCreateSeries = () => useAction<SeriesCreateInput, Series>(TasksA.seriesCreate, { formErrors: true })
export const useStopSeries = () => useAction<{ series_id: string }, { ok: boolean }>(TasksA.seriesStop)

/** Open statuses (shown by default). */
export const OPEN_STATUSES: TaskStatus[] = ['todo', 'in_progress', 'review']
